// src/SetupWizard/Step10ReviewPreference.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

function Step10ReviewPreference({ reviewPreference, onChange }) {
  const [showInfo, setShowInfo] = useState(true);

  const options = [
    {
      value: true,
      label: "Review Before Publishing",
      icon: <ScheduleIcon />,
      description: "Manually review and approve each post before it goes live",
      benefits: [
        "Full control over content",
        "Ability to make final edits",
        "Quality assurance"
      ]
    },
    {
      value: false,
      label: "Automatic Publishing",
      icon: <CheckIcon />,
      description: "Posts will be published automatically according to your schedule",
      benefits: [
        "Hands-off operation",
        "Consistent posting schedule",
        "Time-saving"
      ]
    }
  ];

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 10: Review Preferences
      </Typography>

      <Collapse in={showInfo}>
        <Alert 
          severity="info"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowInfo(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          Choose how you want to handle post publishing
        </Alert>
      </Collapse>

      {/* Options Selection */}
      <Stack spacing={2}>
        {options.map((option) => (
          <Paper
            key={option.value.toString()}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: theme => 
                reviewPreference === option.value 
                  ? `2px solid ${theme.palette.primary.main}`
                  : '2px solid transparent',
              bgcolor: reviewPreference === option.value ? 'action.selected' : 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover',
              },
              transition: 'all 0.2s ease'
            }}
            onClick={() => onChange(option.value)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              {option.icon}
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {option.label}
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {option.description}
            </Typography>

            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Benefits:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {option.benefits.map((benefit, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      px: 1,
                      py: 0.5,
                      typography: 'caption',
                      color: 'text.secondary',
                      bgcolor: 'background.default'
                    }}
                  >
                    {benefit}
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* Tips Section */}
      <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" />
          Pro Tip:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          You can always change your review preferences later in the settings. Start with the option that best matches your current availability and workflow.
        </Typography>
      </Paper>
    </Box>
  );
}

export default Step10ReviewPreference;
